import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, HostBinding, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {MatTabsModule} from '@angular/material/tabs';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {filter, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

import {GetAllUsersAction} from '../../core/store/users/users.action';
import {GetTeamsAction} from '../../core/store/teams/teams.action';
import {GetLensesAction} from '../../core/store/lenses/lenses.action';
import {GetAllRolesAction} from '../../core/store/roles/roles.action';
import {FetchSkillsAction} from '../../core/store/skills/skills.action';

@Component({
  selector: 'people-main-tabs',
  standalone: true,
  imports: [CommonModule, MatTabsModule, RouterOutlet],
  templateUrl: './people-main-tabs.component.html',
  styleUrl: './people-main-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleMainTabsComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();
  private store$ = inject(Store);

  peopleTabs = signal([
    {label: 'People', route: 'users'},
    {label: 'Teams', route: 'teams'},
    {label: 'Roles', route: 'roles'},
    {label: 'Lenses', route: 'lenses'},
  ]);

  selectedTabIndex = 0;

  ngOnInit(): void {
    this.store$.dispatch(new GetAllUsersAction({}));
    this.store$.dispatch(new GetTeamsAction({}));
    this.store$.dispatch(new GetLensesAction({}));
    this.store$.dispatch(new GetAllRolesAction({}));
    this.store$.dispatch(new FetchSkillsAction({}));

    this.setCurrentRoute();
    this.onRouteChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRouteChanges(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setCurrentRoute();
      });
  }

  setCurrentRoute(): void {
    const buildFullPath = (route: ActivatedRoute): string => {
      if (!route || !route.snapshot) return '';
      const urlSegment = route.snapshot.url.map(segment => segment.path).join('/');
      const childPath = buildFullPath(route.firstChild);
      return [urlSegment, childPath].filter(Boolean).join('/');
    };

    const fullPath = buildFullPath(this.route);
    const tabIndex = this.peopleTabs().findIndex(tab => tab.route === fullPath.split('/').pop());
    if (tabIndex !== -1) {
      this.selectedTabChange(tabIndex);
    } else {
      this.selectedTabChange(0);
    }
  }

  selectedTabChange(index: number) {
    this.selectedTabIndex = index;
    const selectedTab = this.peopleTabs()[index];
    this.router.navigate([selectedTab.route.toLowerCase()], {
      relativeTo: this.route.parent,
    });
  }
}
